import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, AlertCircle, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useActiveWorkflows } from '../context/ActiveWorkflowsContext';

function elapsed(startedAt) {
  if (!startedAt) return '';
  // Roleserver writes naive UTC strings (no 'Z'). Date() treats those as local
  // time, which adds one (BST/CET) hour of phantom elapsed time. Force UTC.
  const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(startedAt) ? startedAt : `${startedAt}Z`;
  const start = new Date(iso).getTime();
  if (Number.isNaN(start)) return '';
  const ms = Date.now() - start;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const days = Math.floor(hr / 24);
  return `${days}d`;
}

function rowMeta(workflow) {
  if (workflow.needs_input) {
    return {
      label: 'Needs input',
      icon: AlertCircle,
      iconClass: 'text-amber-500',
      pillClass: 'bg-amber-100 text-amber-700',
    };
  }
  if (workflow.status === 'running') {
    return {
      label: 'Running',
      icon: Loader2,
      iconClass: 'text-blue-500 animate-spin',
      pillClass: 'bg-blue-100 text-blue-700',
    };
  }
  if (workflow.status === 'paused') {
    return {
      label: 'Paused',
      icon: AlertCircle,
      iconClass: 'text-gray-500',
      pillClass: 'bg-gray-100 text-gray-700',
    };
  }
  if (workflow.status === 'completed') {
    return {
      label: 'Completed',
      icon: CheckCircle,
      iconClass: 'text-green-500',
      pillClass: 'bg-green-100 text-green-700',
    };
  }
  if (workflow.status === 'failed') {
    return {
      label: 'Failed',
      icon: XCircle,
      iconClass: 'text-red-500',
      pillClass: 'bg-red-100 text-red-700',
    };
  }
  return {
    label: workflow.status || 'Unknown',
    icon: Activity,
    iconClass: 'text-gray-400',
    pillClass: 'bg-gray-100 text-gray-700',
  };
}

export default function ActiveFlowsMenu() {
  const navigate = useNavigate();
  const { running, paused, recentlyCompleted, acknowledge } = useActiveWorkflows();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Re-render every 30s so elapsed times stay roughly current.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const activeCount = running.length + paused.length;
  const needsAttention = paused.some((w) => w.needs_input) || recentlyCompleted.length > 0;
  const totalBadge = activeCount + recentlyCompleted.length;

  const pillColor = (() => {
    if (paused.some((w) => w.needs_input)) return 'bg-amber-500';
    if (recentlyCompleted.some((w) => w.status === 'failed')) return 'bg-red-500';
    if (recentlyCompleted.length > 0) return 'bg-green-500';
    if (running.length > 0) return 'bg-blue-500';
    return 'bg-gray-500';
  })();

  const handleRowClick = (workflow, view) => {
    if (workflow.status === 'completed' || workflow.status === 'failed') {
      acknowledge(workflow.workflow_id);
    }
    navigate(`/runs/open?workflow=${workflow.workflow_id}&view=${view}`);
    setOpen(false);
  };

  if (totalBadge === 0) {
    return null; // Nothing to show — keep the header tidy.
  }

  const rows = [
    ...paused.filter((w) => w.needs_input),
    ...running,
    ...paused.filter((w) => !w.needs_input),
    ...recentlyCompleted,
  ];

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 rounded-[10px] text-pencil hover:text-ink hover:bg-hl-soft transition-colors duration-150 text-sm focus-ring"
        aria-label={`Active flows (${totalBadge})`}
      >
        <Activity size={16} />
        <span className="hidden sm:inline">Flows</span>
        <span
          className={`${pillColor} text-white text-xs font-semibold rounded-full px-1.5 py-0.5 min-w-[20px] text-center ${
            needsAttention ? 'ring-2 ring-white/20' : ''
          }`}
        >
          {totalBadge}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 max-h-[480px] overflow-y-auto">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-100">
            Active flows
          </div>
          {rows.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-400 text-center">
              No active flows.
            </div>
          ) : (
            rows.map((w) => {
              const meta = rowMeta(w);
              const Icon = meta.icon;
              const isCompletedRow = w.status === 'completed' || w.status === 'failed';
              const targetView = isCompletedRow ? 'history' : 'visualization';
              return (
                <div
                  key={`${w.workflow_id}:${w.status}:${w.needs_input ? 'i' : 'n'}`}
                  className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0"
                  onClick={() => handleRowClick(w, targetView)}
                >
                  <div className="flex items-start gap-2">
                    <Icon size={16} className={`${meta.iconClass} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {w.task_description || w.task_key || 'Workflow'}
                        </p>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {elapsed(w.started_at)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span
                          className={`${meta.pillClass} text-xs font-medium rounded-full px-2 py-0.5`}
                        >
                          {meta.label}
                        </span>
                        {isCompletedRow && (
                          <span className="text-xs text-blue-600 font-medium">
                            View results →
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
